import { BRSR_QUESTIONS } from '../../data/brsr/questions'
import type {
  ActionItem,
  AnswerMap,
  ApplicabilityAssessment,
  BrsrQuestion,
  Gap,
  GapPriority,
  GapRelation,
  ReviewItem,
  Strength,
} from '../../data/brsr/types'
import { responseLabel } from './labels'
import { questionInScore } from './scoring'
import { isQuestionVisible } from './visibility'

const PRIORITY_ORDER: GapPriority[] = ['p1', 'p2', 'p3', 'optional']

function elevated(question: BrsrQuestion, applicability: ApplicabilityAssessment): boolean {
  if (question.indicatorClass === 'leadership') return false
  if (question.dimension === 'value_chain') return false
  if (question.dimension === 'brsr_core') {
    return applicability.coreDisclosureRelevant || applicability.coreAssessmentLikely
  }
  return applicability.filingLikely || applicability.coreAssessmentLikely
}

export function priorityFor(question: BrsrQuestion, value: string, applicability: ApplicabilityAssessment): GapPriority | null {
  if (!question.dimension) return null
  const weak = value === 'not_in_place' || value === 'partial' || value === 'weakly_controlled' || value === 'not_sure'
  if (!weak) return null

  if (question.indicatorClass === 'leadership') return 'optional'

  if (question.dimension === 'value_chain') {
    if (value === 'weakly_controlled') return 'p3'
    return applicability.valueChainBasis === 'voluntary' || applicability.valueChainBasis === 'customer_driven' ? 'p2' : 'p3'
  }

  if (value === 'not_sure') return question.indicatorClass === 'essential' ? 'p2' : 'p3'
  if (value === 'not_in_place' && question.indicatorClass === 'essential' && elevated(question, applicability)) return 'p1'
  if (value === 'partial' && question.indicatorClass === 'essential' && elevated(question, applicability)) return 'p2'
  if (value === 'not_in_place' && question.indicatorClass === 'essential') return 'p2'
  if (value === 'weakly_controlled' && question.indicatorClass === 'essential') return 'p2'
  if (value === 'partial' || value === 'weakly_controlled') return 'p3'
  return 'p3'
}

export function relationFor(question: BrsrQuestion, applicability: ApplicabilityAssessment): GapRelation {
  if (question.indicatorClass === 'leadership') return 'voluntary_practice'
  if (question.dimension === 'value_chain') {
    if (applicability.valueChainBasis === 'customer_driven') return 'customer_driven'
    if (applicability.valueChainBasis === 'voluntary') return 'voluntary_practice'
    if (applicability.valueChainBasis === 'unknown') return 'unresolved_applicability'
    return 'voluntary_practice'
  }
  if (applicability.overallStatus === 'requires_verification' && (question.dimension === 'disclosure' || question.dimension === 'brsr_core' || question.dimension === 'environmental')) {
    return 'unresolved_applicability'
  }
  if (applicability.filingLikely || applicability.coreDisclosureRelevant || applicability.coreAssessmentLikely) {
    return 'applicable_requirement'
  }
  return 'voluntary_practice'
}

export function buildGaps(responses: AnswerMap, applicability: ApplicabilityAssessment): Gap[] {
  const gaps: Gap[] = []
  for (const question of BRSR_QUESTIONS) {
    if (!questionInScore(question, responses)) continue
    const value = responses[question.id]
    if (typeof value !== 'string') continue
    const priority = priorityFor(question, value, applicability)
    if (!priority || priority === 'optional' && question.required === false && value === 'not_sure') continue
    if (!priority) continue
    if (value === 'not_sure') continue
    gaps.push({
      questionId: question.id,
      section: question.section,
      principle: question.principle,
      label: question.label,
      responseLabel: responseLabel(question.id, responses),
      whyItMatters: question.whyItMatters || 'This indicator is part of the readiness assessment.',
      action: question.action || 'Assign an owner and document the data source.',
      priority,
      relation: relationFor(question, applicability),
      sourceIds: question.regulatorySourceIds ?? [],
    })
  }
  return gaps.sort((a, b) => PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority))
}

export function buildReviewItems(responses: AnswerMap): ReviewItem[] {
  const items: ReviewItem[] = []
  for (const question of BRSR_QUESTIONS) {
    if (!isQuestionVisible(question, responses) || !question.dimension) continue
    const value = responses[question.id]
    if (value === 'not_sure') {
      items.push({
        questionId: question.id,
        section: question.section,
        label: question.label,
        responseLabel: 'Not sure',
        reason: 'Unknown answers are excluded from the score. They are not treated as ready.',
        priority: question.indicatorClass === 'essential' ? 'p2' : 'p3',
      })
    }
    if (value === 'not_applicable' && question.naNeedsReview) {
      items.push({
        questionId: question.id,
        section: question.section,
        label: question.label,
        responseLabel: 'Not applicable',
        reason: 'Not applicable was not scored. Confirm it is genuinely outside the BRSR indicator before omitting it from the disclosure.',
        priority: 'p2',
      })
    }
  }
  return items
}

export function buildStrengths(responses: AnswerMap): Strength[] {
  const strengths: Strength[] = []
  for (const question of BRSR_QUESTIONS) {
    if (!questionInScore(question, responses)) continue
    const value = responses[question.id]
    if (value !== 'documented' && value !== 'repeatable') continue
    strengths.push({
      questionId: question.id,
      section: question.section,
      label: question.label,
      responseLabel: responseLabel(question.id, responses),
    })
  }
  return strengths
}

export function buildActions(gaps: Gap[], reviewItems: ReviewItem[]): ActionItem[] {
  const fromGaps: ActionItem[] = gaps
    .filter((gap) => gap.priority !== 'optional')
    .map((gap) => ({
      id: gap.questionId,
      priority: gap.priority,
      title: gap.label,
      detail: gap.action,
      section: gap.section,
      relation: gap.relation,
    }))
  const fromReview: ActionItem[] = reviewItems.map((item) => ({
    id: `review-${item.questionId}`,
    priority: item.priority,
    title: `Confirm: ${item.label}`,
    detail: item.reason,
    section: item.section,
    relation: 'unresolved_applicability' as const,
  }))
  const optional = gaps
    .filter((gap) => gap.priority === 'optional')
    .slice(0, 5)
    .map((gap) => ({
      id: gap.questionId,
      priority: gap.priority,
      title: gap.label,
      detail: gap.action,
      section: gap.section,
      relation: gap.relation,
    }))
  return [...fromGaps, ...fromReview, ...optional].sort(
    (a, b) => PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority),
  )
}

export function keyFindings(gaps: Gap[], reviewItems: ReviewItem[]): Gap[] {
  if (gaps.length >= 3) return gaps.slice(0, 3)
  const extras: Gap[] = reviewItems.slice(0, 3 - gaps.length).map((item) => ({
    questionId: item.questionId,
    section: item.section,
    label: item.label,
    responseLabel: item.responseLabel,
    whyItMatters: item.reason,
    action: item.reason,
    priority: item.priority,
    relation: 'unresolved_applicability',
    sourceIds: [],
  }))
  return [...gaps, ...extras].slice(0, 3)
}
